import React, { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import CategoryManager from "../components/CategoryManager";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";
import { Textarea } from "../components/ui/textarea";
import {
  Plus,
  Pencil,
  Trash2,
  Loader2,
  Briefcase,
  Package,
  CreditCard,
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { formatCurrency } from "../lib/utils";
import { UnderConstructionDialog } from "../components/UnderConstructionDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ServicesAndGoods = () => {
  const { isOfficeManager } = useAuth();
  const [activeTab, setActiveTab] = useState("services");
  // Phase F.6 Batch C — LIVE POS launcher stub (Under Construction until greenlit)
  const [posDialogOpen, setPosDialogOpen] = useState(false);
  
  // Services state
  const [services, setServices] = useState([]);
  const [serviceDialogOpen, setServiceDialogOpen] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [serviceForm, setServiceForm] = useState({
    name: "",
    description: "",
    base_rate: 0,
    rate_type: "net",
    formula: "base_rate * quantity",
    unit: "per_hour",
  });
  
  // Goods state
  const [goods, setGoods] = useState([]);
  const [goodsDialogOpen, setGoodsDialogOpen] = useState(false);
  const [editingGood, setEditingGood] = useState(null);
  const [goodsForm, setGoodsForm] = useState({
    name: "",
    description: "",
    sku: "",
    category: "",
    unit_price: 0,
    cost_price: 0,
    stock_quantity: 0,
    unit: "each",
  });
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [servicesRes, goodsRes] = await Promise.all([
        axios.get(`${API}/services`),
        axios.get(`${API}/goods`).catch(() => ({ data: [] })),
      ]);
      setServices(servicesRes.data);
      setGoods(goodsRes.data);
    } catch (error) {
      toast.error("Failed to fetch data");
    } finally {
      setLoading(false);
    }
  };

  // Service handlers
  const handleOpenServiceDialog = (service = null) => {
    if (service) {
      setEditingService(service);
      setServiceForm({
        name: service.name,
        description: service.description || "",
        base_rate: service.base_rate,
        rate_type: service.rate_type || "net",
        formula: service.formula || "",
        unit: service.unit,
      });
    } else {
      setEditingService(null);
      setServiceForm({
        name: "",
        description: "",
        base_rate: 0,
        rate_type: "net",
        formula: "base_rate * quantity",
        unit: "per_hour",
      });
    }
    setServiceDialogOpen(true);
  };

  const handleServiceSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editingService) {
        await axios.put(`${API}/services/${editingService.id}`, serviceForm);
        toast.success("Service updated");
      } else {
        await axios.post(`${API}/services`, serviceForm);
        toast.success("Service created");
      }
      setServiceDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Operation failed");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteService = async (id) => {
    if (!window.confirm("Delete this service?")) return;
    try {
      await axios.delete(`${API}/services/${id}`);
      toast.success("Service deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete service");
    }
  };

  // Goods handlers
  const handleOpenGoodsDialog = (good = null) => {
    if (good) {
      setEditingGood(good);
      setGoodsForm({
        name: good.name,
        description: good.description || "",
        sku: good.sku || "",
        category: good.category || "",
        unit_price: good.unit_price || 0,
        cost_price: good.cost_price || 0,
        stock_quantity: good.stock_quantity || 0,
        unit: good.unit || "each",
      });
    } else {
      setEditingGood(null);
      setGoodsForm({
        name: "",
        description: "",
        sku: "",
        category: "",
        unit_price: 0,
        cost_price: 0,
        stock_quantity: 0,
        unit: "each",
      });
    }
    setGoodsDialogOpen(true);
  };

  const handleGoodsSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editingGood) {
        await axios.put(`${API}/goods/${editingGood.id}`, goodsForm);
        toast.success("Item updated");
      } else {
        await axios.post(`${API}/goods`, goodsForm);
        toast.success("Item created");
      }
      setGoodsDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Operation failed");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteGood = async (id) => {
    if (!window.confirm("Delete this item?")) return;
    try {
      await axios.delete(`${API}/goods/${id}`);
      toast.success("Item deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete item");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-in" data-testid="services-goods-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">
            Services & Goods
          </h1>
          <p className="text-muted-foreground mt-1">
            Manage your service offerings and inventory items.
          </p>
        </div>
        {/* Phase F.6 Batch C — LIVE POS launcher (Under Construction stub). */}
        {isOfficeManager && (
          <Button
            variant="outline"
            onClick={() => setPosDialogOpen(true)}
            className="border-emerald-500/60 text-emerald-700 hover:bg-emerald-50 dark:text-emerald-300 dark:hover:bg-emerald-950/40"
            data-testid="live-pos-btn"
          >
            <CreditCard className="h-4 w-4 mr-2" />
            LIVE POS
            <span className="ml-2 text-[10px] font-bold uppercase rounded-full bg-amber-500/15 text-amber-700 px-1.5 py-0.5">UC</span>
          </Button>
        )}
      </div>
      <UnderConstructionDialog
        open={posDialogOpen}
        onOpenChange={setPosDialogOpen}
        featureName="LIVE Point-of-Sale"
      />

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-2 max-w-md">
          <TabsTrigger value="services" data-testid="services-tab">
            <Briefcase className="h-4 w-4 mr-2" />
            Services
          </TabsTrigger>
          <TabsTrigger value="goods" data-testid="goods-tab">
            <Package className="h-4 w-4 mr-2" />
            Goods
          </TabsTrigger>
        </TabsList>

        {/* Services Tab */}
        <TabsContent value="services" className="space-y-4">
          <div className="flex justify-end">
            {isOfficeManager && (
              <Button onClick={() => handleOpenServiceDialog()} data-testid="add-service-btn">
                <Plus className="h-4 w-4 mr-2" />
                Add Service
              </Button>
            )}
          </div>

          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Service</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead>Base Rate</TableHead>
                    <TableHead>Unit</TableHead>
                    <TableHead>Formula</TableHead>
                    {isOfficeManager && <TableHead className="w-20">Actions</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {services.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={isOfficeManager ? 6 : 5} className="text-center py-8 text-muted-foreground">
                        No services defined yet
                      </TableCell>
                    </TableRow>
                  ) : (
                    services.map((service) => (
                      <TableRow key={service.id} data-testid={`service-row-${service.id}`}>
                        <TableCell className="font-medium">
                          <div className="flex items-center gap-2">
                            <div className="h-8 w-8 rounded-lg bg-primary/10 flex items-center justify-center">
                              <Briefcase className="h-4 w-4 text-primary" />
                            </div>
                            {service.name}
                          </div>
                        </TableCell>
                        <TableCell className="max-w-xs truncate">{service.description || "-"}</TableCell>
                        <TableCell>{formatCurrency(service.base_rate)}</TableCell>
                        <TableCell className="capitalize">{service.unit?.replace(/_/g, " ")}</TableCell>
                        <TableCell className="font-mono text-xs">{service.formula || "-"}</TableCell>
                        {isOfficeManager && (
                          <TableCell>
                            <div className="flex items-center gap-1">
                              <Button variant="ghost" size="sm" onClick={() => handleOpenServiceDialog(service)}>
                                <Pencil className="h-4 w-4" />
                              </Button>
                              <Button variant="ghost" size="sm" className="text-red-500" onClick={() => handleDeleteService(service.id)}>
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        )}
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Goods Tab */}
        <TabsContent value="goods" className="space-y-4">
          <div className="flex justify-end">
            {isOfficeManager && (
              <Button onClick={() => handleOpenGoodsDialog()} data-testid="add-goods-btn">
                <Plus className="h-4 w-4 mr-2" />
                Add Item
              </Button>
            )}
          </div>

          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Item</TableHead>
                    <TableHead>SKU</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>Unit Price</TableHead>
                    <TableHead>Cost</TableHead>
                    <TableHead>Stock</TableHead>
                    {isOfficeManager && <TableHead className="w-20">Actions</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {goods.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={isOfficeManager ? 7 : 6} className="text-center py-8 text-muted-foreground">
                        No items defined yet
                      </TableCell>
                    </TableRow>
                  ) : (
                    goods.map((good) => (
                      <TableRow key={good.id} data-testid={`goods-row-${good.id}`}>
                        <TableCell className="font-medium">
                          <div className="flex items-center gap-2">
                            <div className="h-8 w-8 rounded-lg bg-orange-500/10 flex items-center justify-center">
                              <Package className="h-4 w-4 text-orange-500" />
                            </div>
                            <div>
                              <p>{good.name}</p>
                              {good.description && <p className="text-xs text-muted-foreground">{good.description}</p>}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="font-mono text-sm">{good.sku || "-"}</TableCell>
                        <TableCell>{good.category || "-"}</TableCell>
                        <TableCell>{formatCurrency(good.unit_price)}</TableCell>
                        <TableCell>{formatCurrency(good.cost_price)}</TableCell>
                        <TableCell>{good.stock_quantity} {good.unit}</TableCell>
                        {isOfficeManager && (
                          <TableCell>
                            <div className="flex items-center gap-1">
                              <Button variant="ghost" size="sm" onClick={() => handleOpenGoodsDialog(good)}>
                                <Pencil className="h-4 w-4" />
                              </Button>
                              <Button variant="ghost" size="sm" className="text-red-500" onClick={() => handleDeleteGood(good.id)}>
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        )}
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Service Dialog */}
      <Dialog open={serviceDialogOpen} onOpenChange={setServiceDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingService ? "Edit Service" : "Add Service"}</DialogTitle>
            <DialogDescription>Define a service with pricing details.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleServiceSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="service-name">Name *</Label>
              <Input
                id="service-name"
                value={serviceForm.name}
                onChange={(e) => setServiceForm({ ...serviceForm, name: e.target.value })}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="service-description">Description</Label>
              <Textarea
                id="service-description"
                value={serviceForm.description}
                onChange={(e) => setServiceForm({ ...serviceForm, description: e.target.value })}
                rows={2}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="service-rate">Base Rate ($) *</Label>
                <Input
                  id="service-rate"
                  type="number"
                  step="0.01"
                  inputMode="decimal"
                  value={serviceForm.base_rate}
                  onChange={(e) => setServiceForm({ ...serviceForm, base_rate: parseFloat(e.target.value) || 0 })}
                  required
                />
                {/* iter141 Beta P2 fix — "Should Base Rate be net or gross?"
                    Explicit toggle so testers/users don't have to guess. */}
                <div className="flex items-center gap-3 pt-1">
                  <Label className="text-xs text-muted-foreground">Type</Label>
                  <label className="flex items-center gap-1 text-xs cursor-pointer">
                    <input
                      type="radio"
                      name="rate-type"
                      data-testid="rate-type-net"
                      checked={serviceForm.rate_type === "net"}
                      onChange={() => setServiceForm({ ...serviceForm, rate_type: "net" })}
                    />
                    Net (pre-tax)
                  </label>
                  <label className="flex items-center gap-1 text-xs cursor-pointer">
                    <input
                      type="radio"
                      name="rate-type"
                      data-testid="rate-type-gross"
                      checked={serviceForm.rate_type === "gross"}
                      onChange={() => setServiceForm({ ...serviceForm, rate_type: "gross" })}
                    />
                    Gross (tax-inclusive)
                  </label>
                </div>
                <p className="text-[11px] text-muted-foreground">
                  Net = tax is added on top at quote/invoice time. Gross = the shown price already includes tax.
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="service-unit">Unit</Label>
                <CategoryManager
                  categoryType="unit"
                  label="Unit Type"
                  onCategoriesChange={() => {}}
                  showAsDropdown={true}
                  selectedValue={serviceForm.unit}
                  onSelectionChange={(v) => setServiceForm({ ...serviceForm, unit: v })}
                  allowAll={false}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="service-formula">Pricing Formula</Label>
              <Input
                id="service-formula"
                value={serviceForm.formula}
                onChange={(e) => setServiceForm({ ...serviceForm, formula: e.target.value })}
                placeholder="e.g., base_rate * quantity * 1.2"
              />
              <p className="text-xs text-muted-foreground">Optional formula for calculating prices</p>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setServiceDialogOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={saving}>
                {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                {editingService ? "Update" : "Create"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Goods Dialog */}
      <Dialog open={goodsDialogOpen} onOpenChange={setGoodsDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingGood ? "Edit Item" : "Add Item"}</DialogTitle>
            <DialogDescription>Define an inventory item with pricing and stock details.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleGoodsSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="goods-name">Name *</Label>
                <Input
                  id="goods-name"
                  value={goodsForm.name}
                  onChange={(e) => setGoodsForm({ ...goodsForm, name: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="goods-sku">SKU</Label>
                <Input
                  id="goods-sku"
                  value={goodsForm.sku}
                  onChange={(e) => setGoodsForm({ ...goodsForm, sku: e.target.value })}
                  placeholder="e.g., PROD-001"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="goods-description">Description</Label>
              <Textarea
                id="goods-description"
                value={goodsForm.description}
                onChange={(e) => setGoodsForm({ ...goodsForm, description: e.target.value })}
                rows={2}
              />
            </div>
            <div className="space-y-2">
              <Label>Category</Label>
              <CategoryManager
                categoryType="goods"
                label="Category"
                onCategoriesChange={() => {}}
                showAsDropdown={true}
                selectedValue={goodsForm.category}
                onSelectionChange={(v) => setGoodsForm({ ...goodsForm, category: v })}
                allowAll={false}
              />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="goods-unit-price">Unit Price ($) *</Label>
                <Input
                  id="goods-unit-price"
                  type="number"
                  step="0.01"
                  value={goodsForm.unit_price}
                  onChange={(e) => setGoodsForm({ ...goodsForm, unit_price: parseFloat(e.target.value) || 0 })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="goods-cost">Cost ($)</Label>
                <Input
                  id="goods-cost"
                  type="number"
                  step="0.01"
                  value={goodsForm.cost_price}
                  onChange={(e) => setGoodsForm({ ...goodsForm, cost_price: parseFloat(e.target.value) || 0 })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="goods-stock">Stock Qty</Label>
                <Input
                  id="goods-stock"
                  type="number"
                  value={goodsForm.stock_quantity}
                  onChange={(e) => setGoodsForm({ ...goodsForm, stock_quantity: parseInt(e.target.value) || 0 })}
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setGoodsDialogOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={saving}>
                {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                {editingGood ? "Update" : "Create"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ServicesAndGoods;
